import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { settingsApi, type UpdateCRMSettingsInput } from '../../shared/services/crm.service';
import { crmKeys } from '../../shared/constants/queryKeys';

export function useSettings() {
  return useQuery({
    queryKey: crmKeys.settings,
    queryFn: () => settingsApi.get(),
    staleTime: 1000 * 30,
    refetchOnMount: true,
    refetchOnWindowFocus: 'always',
  });
}

/**
 * BUG-CRM-19 — Narrow hook for the lead/contact hot/warm score badge.
 *
 * Use this on any page where non-admin roles (sales_rep) need scoring
 * thresholds — calling {@link useSettings} from those pages now 403s
 * because the broad `crm:setting:read:tenant` permission was demoted.
 * This hook hits `GET /crm/settings/public-config`, which returns only
 * the three fields the badge needs.
 */
export function usePublicScoringConfig() {
  return useQuery({
    queryKey: [...crmKeys.settings, 'public-config'] as const,
    queryFn: () => settingsApi.getPublicConfig(),
    staleTime: 1000 * 60,
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCRMSettingsInput) => settingsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.settings });
      toast.success('Settings updated');
    },
    onError: () => {
      toast.error('Failed to update settings');
    },
  });
}
